import type Decimal from 'decimal.js';
import { Banknote, Check, Pencil, TrendingDown, TrendingUp, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Button, NumberInput } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { useBaseCurrency } from '../../lib/currenciesStore.js';
import { effectiveRateFor, formatRate, loadExchangeRates, saveExchangeRate, type RateRow } from '../../lib/exchangeRates.js';
import { formatDate } from '../../lib/intl.js';
import { normalizeNumericInput, safeDecimal } from '../../lib/numerals.js';

/** How much history the popover shows — a month of daily changes, roughly. */
const HISTORY_ROWS = 8;

/**
 * Titlebar exchange-rate chrome — the palette switcher's sibling. The button
 * shows for anyone with `exchange_rates.read` on a multi-currency tenant; the
 * popover shows today's rate per foreign currency (editable in place with
 * `exchange_rates.write` — same POST /exchange-rates as the dashboard card and
 * the journal form) plus the recent CHANGES of the selected pair, each with
 * its delta — "who moved the dollar and when" at a glance from any page.
 */
export function RateSwitcher(): JSX.Element | null {
  const { t, i18n } = useTranslation();
  const canRead = usePermission('exchange_rates.read');
  const canWrite = usePermission('exchange_rates.write');
  const base = useBaseCurrency();

  const [open, setOpen] = useState(false);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [rates, setRates] = useState<RateRow[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  // One cheap fetch on mount decides whether the button exists at all —
  // a single-currency shop never sees it.
  useEffect(() => {
    if (!canRead || !base) return undefined;
    let on = true;
    void api().get<{ items: Array<{ code: string; isActive: boolean }> }>('/currencies')
      .then((r) => { if (on) setCodes(r.data.items.filter((c) => c.isActive && c.code !== base).map((c) => c.code)); })
      .catch(() => { if (on) setCodes([]); });
    return () => { on = false; };
  }, [canRead, base]);

  const reload = useCallback(async (): Promise<void> => {
    setRates(await loadExchangeRates());
  }, []);

  // Rates load when the popover opens (and refresh on every open — the rate
  // may have been changed elsewhere since).
  useEffect(() => {
    if (open) void reload();
  }, [open, reload]);

  // Close on outside-click / Escape — same manners as the palette popover.
  useEffect(() => {
    if (!open) return undefined;
    function onDoc(e: MouseEvent): void {
      const target = e.target as Node;
      if (popRef.current?.contains(target) || btnRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!canRead || !base || codes === null || codes.length === 0) return null;

  const todayStr = new Date().toISOString().slice(0, 10);
  const label = t('dashboard.exchangeRate', { defaultValue: 'Exchange rate' });

  // Working set: pairs with a rate history lead; bare activations only when
  // nothing has a rate yet (so the very first rate can be typed here too).
  const rated = rates ? codes.filter((c) => rates.some(
    (r) => (r.fromCurrency === c && r.toCurrency === base) || (r.fromCurrency === base && r.toCurrency === c),
  )) : [];
  const shown = (rated.length > 0 ? rated : codes).slice(0, 4);
  const active = selected && shown.includes(selected) ? selected : shown[0] ?? null;

  /** The pair's own writes, newest first (server order preserved by filter). */
  const history = active && rates
    ? rates.filter((r) => (r.fromCurrency === active && r.toCurrency === base) || (r.fromCurrency === base && r.toCurrency === active)).slice(0, HISTORY_ROWS)
    : [];

  /** Display a stored row in CUR→BASE terms even when it was saved inverted. */
  const rowRate = (r: RateRow): Decimal => (r.fromCurrency === base
    ? safeDecimal(1).dividedBy(safeDecimal(r.rate))
    : safeDecimal(r.rate));

  const save = async (code: string): Promise<void> => {
    const val = safeDecimal(normalizeNumericInput(draft));
    if (val.lte(0)) return;
    setSaving(true);
    try {
      await saveExchangeRate(code, base, val.toDecimalPlaces(8).toString(), todayStr);
      setEditing(null);
      await reload();
      toast.success(t('dashboard.rateSaved', { defaultValue: 'Rate saved — used by everything posted from today on.' }));
    } catch {
      toast.error(t('dashboard.rateFailed', { defaultValue: "Couldn't save the rate." }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="icon-btn"
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={label}
        onClick={() => setOpen((o) => !o)}
      >
        <Banknote aria-hidden="true" />
      </button>

      {open && (
        <div ref={popRef} className="pal-pop" role="dialog" aria-label={label}>
          <div className="ph">{label}</div>

          <div className="flex flex-col gap-2">
            {shown.map((code) => {
              const current = rates ? effectiveRateFor(rates, code, base, todayStr) : null;
              const isEditing = editing === code;
              return (
                <div
                  key={code}
                  className={`flex flex-wrap items-center gap-2 rounded-lg border p-2 ${code === active ? 'border-brand/50 bg-brand/5' : 'border-transparent'}`}
                  onClick={() => setSelected(code)}
                  onKeyDown={(e) => { if (e.key === 'Enter') setSelected(code); }}
                  role="button"
                  tabIndex={0}
                >
                  <span className="font-mono text-sm text-fg-muted">1 {code} =</span>
                  {isEditing ? (
                    <>
                      <div className="w-28">
                        <NumberInput
                          autoFocus
                          className="text-end font-mono"
                          value={draft}
                          onValueChange={setDraft}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); void save(code); }
                            if (e.key === 'Escape') { e.stopPropagation(); setEditing(null); }
                          }}
                          aria-label={label}
                        />
                      </div>
                      <span className="text-sm text-fg-muted">{base}</span>
                      <Button type="button" size="sm" loading={saving} onClick={() => void save(code)} aria-label={t('actions.save')}>
                        <Check className="h-4 w-4" aria-hidden />
                      </Button>
                      <Button type="button" size="sm" variant="ghost" disabled={saving} onClick={() => setEditing(null)} aria-label={t('actions.cancel')}>
                        <X className="h-4 w-4" aria-hidden />
                      </Button>
                    </>
                  ) : (
                    <>
                      <span className="font-mono text-lg font-bold">{current ? formatRate(current, i18n.language) : '—'}</span>
                      <span className="text-sm text-fg-muted">{base}</span>
                      {canWrite && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="ms-auto"
                          onClick={(e) => { e.stopPropagation(); setEditing(code); setDraft(current ? current.toDecimalPlaces(8).toString() : ''); }}
                          aria-label={t('dashboard.editRate', { defaultValue: 'Change the rate' })}
                          title={t('dashboard.editRate', { defaultValue: 'Change the rate' })}
                        >
                          <Pencil className="h-4 w-4" aria-hidden />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {history.length > 0 && (
            <div className="mt-3 border-t border-border pt-2">
              <div className="flex items-center justify-between gap-2">
                <div className="ph">{t('dashboard.rateHistory', { defaultValue: 'Recent changes' })}</div>
                {/* The full story — every change with its exact time and who
                    made it — lives on its own page. */}
                <Link
                  to="/accounting/exchange-rates"
                  className="mb-[11px] text-xs font-semibold text-brand hover:underline"
                  onClick={() => setOpen(false)}
                >
                  {t('actions.viewAll', { defaultValue: 'View all' })}
                </Link>
              </div>
              <ul className="flex flex-col gap-1 text-sm">
                {history.map((r, i) => {
                  const cur = rowRate(r);
                  const older = history[i + 1];
                  const delta = older ? cur.minus(rowRate(older)) : null;
                  return (
                    <li key={`${r.asOf}-${i}`} className="flex items-center gap-2">
                      <span className="w-24 flex-none text-xs text-fg-subtle">{formatDate(r.asOf, { locale: i18n.language })}</span>
                      <span className="font-mono font-semibold">{formatRate(cur, i18n.language)}</span>
                      {delta && !delta.isZero() && (
                        <span className={`flex items-center gap-0.5 font-mono text-xs ${delta.gt(0) ? 'text-success' : 'text-danger'}`}>
                          {delta.gt(0)
                            ? <TrendingUp className="h-3.5 w-3.5" aria-hidden />
                            : <TrendingDown className="h-3.5 w-3.5" aria-hidden />}
                          {formatRate(delta.abs(), i18n.language)}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}
    </>
  );
}
